/**
 * Climate and biome adapter derived from Azgaar Fantasy Map Generator.
 *
 * Adapted from revision 4eb71cfb89bebf987174623f9ccc0485bf328819:
 * - src/generators/temperature-generator.ts
 * - src/generators/precipitation-generator.ts
 * - src/generators/biomes-generator.ts
 *
 * Copyright (c) 2017-2024 Max Haniyeu (Azgaar)
 * SPDX-License-Identifier: MIT
 * See ./LICENSE and ./UPSTREAM.md.
 */

export const AZGAAR_BIOME_NAMES = [
  "Marine",
  "Hot desert",
  "Cold desert",
  "Savanna",
  "Grassland",
  "Tropical seasonal forest",
  "Temperate deciduous forest",
  "Tropical rainforest",
  "Temperate rainforest",
  "Taiga",
  "Tundra",
  "Glacier",
  "Wetland",
] as const;

export const AZGAAR_BIOME_COLORS = ["#466eab", "#fbe79f", "#b5b887", "#d2d082", "#c8d68f", "#b6d95d", "#29bc56", "#7dcb35", "#409c43", "#4b6b32", "#96784b", "#d5e7eb", "#0b9131"] as const;

export interface AzgaarClimateOptions {
  columns: number;
  rows: number;
  heights: ArrayLike<number>;
  seed: string;
  latitudeNorth?: number;
  latitudeSouth?: number;
  temperatureEquator?: number;
  temperatureNorthPole?: number;
  temperatureSouthPole?: number;
  precipitation?: number;
  heightExponent?: number;
  winds?: readonly number[];
  riverFlux?: ArrayLike<number>;
}

export interface AzgaarClimate {
  temperature: Int8Array;
  precipitation: Uint8Array;
  moisture: Uint8Array;
  biome: Uint8Array;
}

const LATITUDE_MODIFIER = [4, 2, 2, 2, 1, 1, 2, 2, 2, 2, 3, 3, 2, 2, 1, 1, 1, .5] as const;
const BIOME_MATRIX = [
  new Uint8Array([1, 1, 1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 10]),
  new Uint8Array([3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 9, 9, 9, 9, 10, 10, 10]),
  new Uint8Array([5, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 9, 9, 9, 9, 9, 10, 10, 10]),
  new Uint8Array([5, 6, 6, 6, 6, 6, 6, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9, 9, 10, 10, 10]),
  new Uint8Array([7, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 8, 9, 9, 9, 9, 9, 9, 9, 10, 10]),
] as const;

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));

function seedHash(seed: string) {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) { hash ^= seed.charCodeAt(index); hash = Math.imul(hash, 16777619); }
  return hash >>> 0;
}

function seededRandom(seed: string) {
  let state = seedHash(seed) || 0x6d2b79f5;
  return () => { state += 0x6d2b79f5; let value = state; value = Math.imul(value ^ value >>> 15, value | 1); value ^= value + Math.imul(value ^ value >>> 7, value | 61); return ((value ^ value >>> 14) >>> 0) / 4294967296; };
}

function neighbors(cell: number, columns: number, rows: number) {
  const x = cell % columns; const y = Math.floor(cell / columns); const result: number[] = [];
  if (x > 0) result.push(cell - 1); if (x < columns - 1) result.push(cell + 1); if (y > 0) result.push(cell - columns); if (y < rows - 1) result.push(cell + columns);
  return result;
}

function calculateTemperature(options: AzgaarClimateOptions) {
  const { columns, rows, heights } = options;
  const result = new Int8Array(columns * rows);
  const equator = options.temperatureEquator ?? 27; const northPole = options.temperatureNorthPole ?? -30; const southPole = options.temperatureSouthPole ?? -15;
  const latitudeNorth = options.latitudeNorth ?? 70; const latitudeSouth = options.latitudeSouth ?? -70; const latitudeSpan = latitudeNorth - latitudeSouth;
  const northTropic = equator - 16 * .15; const northGradient = (northTropic - northPole) / 74;
  const southTropic = equator - 20 * .15; const southGradient = (southTropic - southPole) / 70;
  const exponent = options.heightExponent ?? 1.8;
  const seaTemperature = (latitude: number) => latitude <= 16 && latitude >= -20 ? equator - Math.abs(latitude) * .15 : latitude > 0 ? northTropic - (latitude - 16) * northGradient : southTropic + (latitude + 20) * southGradient;
  const altitudeDrop = (height: number) => height < 20 ? 0 : ((height - 18) ** exponent / 1000) * 6.5;
  for (let row = 0; row < rows; row += 1) {
    const latitude = latitudeNorth - row / Math.max(1, rows - 1) * latitudeSpan; const base = seaTemperature(latitude);
    for (let column = 0; column < columns; column += 1) { const cell = row * columns + column; result[cell] = clamp(Math.round(base - altitudeDrop(heights[cell] ?? 0)), -128, 127); }
  }
  return result;
}

function calculatePrecipitation(options: AzgaarClimateOptions, temperature: Int8Array) {
  const { columns, rows, heights } = options; const count = columns * rows; const result = new Uint8Array(count); const random = seededRandom(`${options.seed}:azgaar:precipitation`);
  const latitudeNorth = options.latitudeNorth ?? 70; const latitudeSouth = options.latitudeSouth ?? -70; const latitudeSpan = latitudeNorth - latitudeSouth;
  const winds = options.winds ?? [225, 45, 225, 315, 135, 315]; const modifier = (count / 10_000) ** .25 * ((options.precipitation ?? 100) / 100);
  type WindBand = [number, number]; const westerly: WindBand[] = []; const easterly: WindBand[] = []; let northerly = 0; let southerly = 0;
  for (let row = 0; row < rows; row += 1) {
    const latitude = latitudeNorth - row / Math.max(1, rows - 1) * latitudeSpan; const band = clamp(Math.floor((Math.abs(latitude) - 1) / 5), 0, LATITUDE_MODIFIER.length - 1); const tier = clamp(Math.floor(Math.abs(latitude - 89) / 30), 0, winds.length - 1); const angle = winds[tier] ?? 225;
    if (angle > 40 && angle < 140) westerly.push([row * columns, LATITUDE_MODIFIER[band]!]);
    if (angle > 220 && angle < 320) easterly.push([row * columns + columns - 1, LATITUDE_MODIFIER[band]!]);
    if (angle > 100 && angle < 260) northerly += 1; if (angle > 280 || angle < 80) southerly += 1;
  }
  const passWind = (sources: WindBand[] | number[], initialMaximum: number, next: number, steps: number) => {
    for (const source of sources) {
      const first = Array.isArray(source) ? source[0] : source; const maximum = Math.min(initialMaximum * (Array.isArray(source) ? source[1] : 1), 255); let humidity = maximum - (heights[first] ?? 0); if (humidity <= 0) continue;
      for (let step = 0, current = first; step < steps && current >= 0 && current < count; step += 1, current += next) {
        if (temperature[current]! < -5) continue; const nextCell = current + next; const currentHeight = heights[current] ?? 0; const nextHeight = nextCell >= 0 && nextCell < count ? heights[nextCell] ?? 0 : currentHeight;
        if (currentHeight < 20) {
          if (nextHeight >= 20) result[nextCell] = clamp(result[nextCell]! + Math.max(humidity / (10 + Math.floor(random() * 11)), 1), 0, 255);
          else { humidity = Math.min(humidity + 5 * modifier, maximum); result[current] = clamp(result[current]! + 5 * modifier, 0, 255); }
          continue;
        }
        const passable = nextHeight <= 85; const normalLoss = Math.max(humidity / Math.max(1, 10 * modifier), 1); const difference = Math.max(nextHeight - currentHeight, 0); const precipitation = passable ? clamp(normalLoss + difference * (nextHeight / 70) ** 2, 1, humidity) : humidity;
        result[current] = clamp(result[current]! + precipitation, 0, 255); humidity = passable ? clamp(humidity - precipitation + (precipitation > 1.5 ? 1 : 0), 0, maximum) : 0;
      }
    }
  };
  if (westerly.length) passWind(westerly, 120 * modifier, 1, columns);
  if (easterly.length) passWind(easterly, 120 * modifier, -1, columns);
  const vertical = northerly + southerly;
  if (northerly && vertical) passWind(Array.from({ length: columns }, (_, index) => index), northerly / vertical * 60 * modifier * 2, columns, rows);
  if (southerly && vertical) passWind(Array.from({ length: columns }, (_, index) => count - columns + index), southerly / vertical * 60 * modifier * 2, -columns, rows);
  return result;
}

function biomeId(moisture: number, temperature: number, height: number, hasRiver: boolean) {
  if (height < 20) return 0; if (temperature < -5) return 11; if (temperature >= 25 && !hasRiver && moisture < 8) return 1;
  if (temperature > -2 && ((moisture > 40 && height < 25) || (moisture > 24 && height > 24 && height < 60))) return 12;
  const moistureBand = Math.min(Math.floor(moisture / 5), 4); const temperatureBand = Math.min(Math.max(20 - temperature, 0), 25);
  return BIOME_MATRIX[moistureBand]![temperatureBand]!;
}

export function generateAzgaarClimate(options: AzgaarClimateOptions): AzgaarClimate {
  const { columns, rows, heights } = options; const count = columns * rows; const temperature = calculateTemperature(options); const precipitation = calculatePrecipitation(options, temperature); const moisture = new Uint8Array(count); const biome = new Uint8Array(count);
  for (let cell = 0; cell < count; cell += 1) {
    const height = heights[cell] ?? 0; const flux = options.riverFlux?.[cell] ?? 0; const hasRiver = flux > 0;
    if (height >= 20) { const values = neighbors(cell, columns, rows).filter(neighbor => (heights[neighbor] ?? 0) >= 20).map(neighbor => precipitation[neighbor]!); const mean = values.length ? values.reduce((sum, value) => sum + value, precipitation[cell]!) / (values.length + 1) : precipitation[cell]!; moisture[cell] = clamp(Math.round(4 + mean + (hasRiver ? Math.max(flux / 10, 2) : 0)), 0, 255); }
    biome[cell] = biomeId(moisture[cell]!, temperature[cell]!, height, hasRiver);
  }
  return { temperature, precipitation, moisture, biome };
}
